import { useState } from "react";

import {
  faCheck,
  faEye,
  faSpinner,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { noop } from "lodash-es";

import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import {
  FloatingFormTitle,
  FloatingFormWindow,
} from "src/cases/caseView/components/FloatingFormWindow";
import { useCasePropertiesUpdatedTable } from "src/cases/caseView/history/events/CasePropertiesTable";
import {
  HistoryEntryGroup,
  HistorySingleEntry,
} from "src/cases/caseView/history/HistoryItemFrame";
import {
  useCaseActionFormSchema,
  useGetCaseTemplateAction,
} from "src/cases/caseView/hooks";
import { InlineTable } from "src/cases/common/InlineTable";
import { useCase } from "src/cases/queries/cases";
import {
  CaseHistoryEvent,
  GroupableEventType,
  HistoryEntryActionGroup as HistoryEntryGroupType,
} from "src/cases/queries/casesTypes";
import { Tooltip } from "src/design-system/Tooltip";
import { CasePageParams } from "src/router/urls";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

const isActionTriggeredEvent = (
  event: CaseHistoryEvent<GroupableEventType>,
): event is CaseHistoryEvent<"action_triggered"> => {
  return event.event_type === "action_triggered";
};

export const ActionTriggeredGroup: React.FC<{
  entry: HistoryEntryGroupType;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  const { events } = entry;
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const { data: caseData } = useCase(caseId);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const getCaseTemplateAction = useGetCaseTemplateAction(caseData);
  const templateVersion = caseData?.data.template_version;

  // Prepare table data for action_completed events with updated_properties
  // This must be called before any early returns to follow React hooks rules
  const completedEvent = events.find(
    (e) => e.event_data.event_type === "action_completed",
  );
  const updatedProperties =
    completedEvent?.event_data.event_type === "action_completed"
      ? completedEvent.event_data.updated_properties
      : undefined;

  const hasUpdatedProperties =
    updatedProperties && Object.keys(updatedProperties).length > 0;

  // Always call the hook unconditionally at the top level
  const { columns, rows } = useCasePropertiesUpdatedTable(
    updatedProperties || {},
    templateVersion,
  );

  const propertiesContent =
    hasUpdatedProperties && rows.length > 0 ? (
      <InlineTable columns={columns} data={rows} />
    ) : undefined;

  const triggeredEvent = events.find(isActionTriggeredEvent);

  // Extract action form data for viewing - must be called before any early returns
  const eventData = triggeredEvent?.event_data;
  const action_key = eventData?.action_key;
  const action_form_data = eventData?.action_form_data;
  const templateAction = getCaseTemplateAction(action_key);
  const actionFormSchema = useCaseActionFormSchema(templateAction);

  if (!triggeredEvent) {
    // Group can not exist without an action_triggered event
    logger.error(
      "ActionTriggeredGroup: Group can not exist without an action_triggered event",
      entry,
    );
    return null;
  }

  const actionName =
    eventData?.action_display_name || eventData?.action_key || "Unknown action";

  const hasFormFields =
    templateAction?.form?.fields && templateAction.form.fields.length > 0;
  const hasFormData =
    action_form_data && Object.keys(action_form_data).length > 0;
  const shouldShowForm = hasFormFields && hasFormData;

  const formViewButton = shouldShowForm ? (
    <FloatingFormWindow
      button={
        <Tooltip delay={250} placement="top-end" title="View form" asChild>
          <Icon
            color="text-gray-500 hover:text-gray-700"
            dataLoc={`action-view-form-${eventData.action_key}`}
            icon={faEye}
            size="sm"
            onClick={() => setIsFormOpen(true)}
          />
        </Tooltip>
      }
      defaultValues={action_form_data}
      disabled={true}
      fields={templateAction.form?.fields ?? []}
      isOpen={isFormOpen}
      schema={actionFormSchema}
      title={
        <FloatingFormTitle
          description={templateAction.description ?? undefined}
          task={{
            // We do not need action id in history, it is just to display the form
            id: "",
            key: eventData.action_key,
            title: actionName,
            isCompleted: true,
          }}
          title={actionName}
          disabled
        />
      }
      onClose={() => setIsFormOpen(false)}
      onSaveAndClose={noop}
    />
  ) : undefined;

  if (events.length === 1) {
    return (
      <HistorySingleEntry
        actorId={triggeredEvent.user_id}
        eventType={triggeredEvent.event_type}
        headline={
          <>
            <Pill size="sm" variant="gray">
              <Pill.Text>{actionName}</Pill.Text>
            </Pill>{" "}
            action was submitted.
          </>
        }
        icon={faSpinner}
        isLast={isLast}
        sideAction={formViewButton}
        timestamp={triggeredEvent.timestamp}
      />
    );
  }

  // Determine icon based on the first (newest) event
  const firstEvent = events[0];
  const groupIcon =
    firstEvent?.event_data.event_type === "action_completed"
      ? faCheck
      : faTriangleExclamation;

  // Map events to items (already in newest-first order)
  const items = events.map((event) => {
    const eventData = event.event_data;

    if (eventData.event_type === "action_completed") {
      return {
        eventType: event.event_type,
        timestamp: event.timestamp,
        headline: (
          <>
            <Pill size="sm" variant="gray">
              <Pill.Text>{actionName}</Pill.Text>
            </Pill>{" "}
            action was completed.
          </>
        ),
        content: isExpanded ? propertiesContent : undefined,
      };
    }

    if (eventData.event_type === "action_failed") {
      return {
        eventType: event.event_type,
        timestamp: event.timestamp,
        headline: (
          <>
            <Pill size="sm" variant="gray">
              <Pill.Text>{actionName}</Pill.Text>
            </Pill>{" "}
            action failed due to an error.
          </>
        ),
      };
    }

    if (eventData.event_type === "action_triggered") {
      return {
        eventType: event.event_type,
        timestamp: event.timestamp,
        headline: (
          <>
            <Pill size="sm" variant="gray">
              <Pill.Text>{actionName}</Pill.Text>
            </Pill>{" "}
            action was submitted.
          </>
        ),
        actorId: event.user_id,
        sideAction: formViewButton,
      };
    }

    // Fallback for unexpected event types
    return {
      eventType: event.event_type,
      timestamp: event.timestamp,
      headline: <>Unknown event</>,
      actorId: event.user_id,
    };
  });

  return (
    <HistoryEntryGroup
      eventType="action_triggered"
      icon={groupIcon}
      isExpanded={isExpanded}
      isLast={isLast}
      items={isExpanded ? items : [items[0]]}
      onToggle={setIsExpanded}
    />
  );
};
